"use client";

import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function PillarServices() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: accordionRef,
    isVisible: accordionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const comparisons = [{
    id: 1,
    index: "01",
    title: "Primary Goal & Outcome",
    seo: "Rank #1 on Google SERP blue links for targeted keywords.",
    geo: "Be recommended as the direct answer by AI assistants (ChatGPT, Claude, Gemini, Perplexity)."
  }, {
    id: 2,
    index: "02",
    title: "User Discovery Behavior",
    seo: "Users scan search results, click multiple links, and compare websites manually.",
    geo: "Users ask AI for a answer to their problems and AI, gives just a few answers. We make you the answer."
  }, {
    id: 3,
    index: "03",
    title: "Content & Entity Authority",
    seo: "Keyword density, backlinks, and on-page technical optimization signals.",
    geo: "Entity consensus, structured digital footprint, reviews, and cross-platform citation depth."
  }, {
    id: 4,
    index: "04",
    title: "Metric for Success",
    seo: "Organic traffic, impressions, click-through rate (CTR), and SERP positions.",
    geo: "AI recommendation frequency, share of voice in LLM responses, and high-intent qualified leads."
  }, {
    id: 5,
    index: "05",
    title: "Search Interface Format",
    seo: "10 blue links, sponsored ads, local map pack, and feature snippets.",
    geo: "Conversational answer panels with citations, source links, and follow-up prompts."
  }, {
    id: 6,
    index: "06",
    title: "Competitive Advantage",
    seo: "Competing for limited front-page real estate against high-DA national websites.",
    geo: "Establishing early entity dominance before local competitors optimize for generative engines."
  }];
  return <section data-section-id="2254" id="pillarservices" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Header Block */}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-default text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--accent-deep-red)"
          }}>
              SEO vs GEO
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
              Ranking isn't the same as being{" "}
              <span className="italic font-normal" style={{
              color: "var(--accent-deep-red)"
            }}>
                recommended
              </span>
              .
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              Traditional SEO was designed to list your website among ten blue links on search engines. Generative Engine Optimization (GEO) positions your business as the single recommended answer when potential clients ask AI assistants who to hire.
            </p>
          </div>
        </div>

        {/* Accordion Comparison Block */}
        <div ref={accordionRef} className={`space-y-4 transition-all duration-700 ease-out ${accordionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {comparisons.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border pb-4 transition-colors duration-200">
              <button type="button" onClick={() => setOpenIndex(openIndex === i ? null : i)} aria-expanded={openIndex === i} className="w-full flex items-center justify-between py-5 text-left group focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-lg">
                <div className="flex items-center gap-4 md:gap-6 pr-4">
                  <span className="font-mono text-sm md:text-base font-bold tracking-wider" style={{
                color: "var(--accent-deep-red)"
              }}>
                    {item.index}
                  </span>
                  <h3 className="font-default font-bold text-xl md:text-2xl text-foreground group-hover:text-foreground/80 transition-colors">
                    {item.title}
                  </h3>
                </div>
                <div className="flex-shrink-0 ml-4 p-2 rounded-lg border border-border bg-card transition-colors duration-200" style={{
              backgroundColor: "var(--card)"
            }}>
                  {openIndex === i ? <Minus className="w-5 h-5 text-foreground" /> : <Plus className="w-5 h-5 text-muted-foreground group-hover:text-foreground" />}
                </div>
              </button>

              {openIndex === i && <div className="pb-6 pt-2 grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-300">
                  <div className="p-5 md:p-6 rounded-lg border border-border bg-card" style={{
              backgroundColor: "var(--card)"
            }}>
                    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Traditional SEO
                    </p>
                    <p className="font-default text-sm md:text-base leading-relaxed text-foreground">
                      {item.seo}
                    </p>
                  </div>
                  <div className="p-5 md:p-6 rounded-lg border border-border relative overflow-hidden bg-card" style={{
              backgroundColor: "var(--card)"
            }}>
                    <div className="absolute top-0 left-0 w-1 h-full" style={{
                backgroundColor: "var(--accent-deep-red)"
              }} />
                    <p className="font-mono text-xs uppercase tracking-wider font-semibold mb-2" style={{
                color: "var(--accent-deep-red)"
              }}>
                      Generative Engine Optimization (GEO)
                    </p>
                    <p className="font-default text-sm md:text-base leading-relaxed text-foreground">
                      {item.geo}
                    </p>
                  </div>
                </div>}
            </div>)}
        </div>

        {/* Closing Block */}
        <div className="mt-16 pt-10 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-default text-base md:text-lg text-muted-foreground max-w-2xl">
            The transition from traditional search to AI recommendation requires structured authority so you are{" "}
            <span className="italic font-semibold" style={{
            color: "var(--accent-deep-red)"
          }}>
              who the AI names first
            </span>
            .
          </p>
          <Link to="/VisibilityCheckupPage" className="inline-flex items-center text-sm md:text-base font-semibold whitespace-nowrap transition-opacity hover:opacity-80" style={{
          color: "var(--accent-deep-red)"
        }}>
            Get your free visibility checkup →
          </Link>
        </div>
      </div>
    </section>;
}